import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SuggestionTypeRow from './suggestion-type-row';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { selectSuggestionsLoading, selectSuggestionsError, selectSuggestedNodes } from '../../store/graph/selectors';
import './style.css';

const SuggestionsPopup = ({ person, onClose, onFetchSuggestions, position }) => {
  const popupRef = useRef();
  const isLoading = useSelector(selectSuggestionsLoading);
  const error = useSelector(selectSuggestionsError);
  const suggestedNodes = useSelector(selectSuggestedNodes);
  const [hasFetched, setHasFetched] = useState(false);
  const prevLoadingRef = useRef(isLoading);

  useOnClickOutside(popupRef, onClose);

  // Auto-close popup when loading completes successfully
  useEffect(() => {
    const loadingJustCompleted = prevLoadingRef.current && !isLoading;
    const wasSuccessful = !error;
    const shouldAutoClose = hasFetched && loadingJustCompleted && wasSuccessful;

    if (shouldAutoClose) {
      onClose();
    }
    
    prevLoadingRef.current = isLoading;
  }, [isLoading, hasFetched, error, onClose]);

  const [signals, setSignals] = useState({
    coattendee: { enabled: true, minEvents: 3 },
    geographic: { enabled: true, radius: 1.0 },
    employer: { enabled: false },
  });

  const handleSignalChange = (signal, key, value) => {
    setSignals(prev => ({
      ...prev,
      [signal]: { ...prev[signal], [key]: value },
    }));
  };

  const handleFetchClick = () => {
    const enabledSignals = Object.keys(signals).filter(key => signals[key].enabled);
    setHasFetched(true);
    onFetchSuggestions(person.id, {
      enabledSignals,
      minSharedEvents: signals.coattendee.minEvents,
      radiusMiles: signals.geographic.radius,
      metroRegion: person.metroRegion || person.MetroRegion,
    });
  };

  const popupStyle = position
    ? { top: `${position.top}px`, left: `${position.left}px` }
    : undefined;

  return (
    <div
      className="suggestions-popup"
      ref={popupRef}
      style={popupStyle}
      role="dialog"
      aria-modal="true"
      aria-labelledby="suggestions-header"
    >
      <div className="suggestions-popup-header" id="suggestions-header">
        Suggestions for {person.name}
        <button type="button" onClick={onClose} className="close-btn">&times;</button>
      </div>
      <p className="suggestions-popup-description">
        Find potential relationships based on shared activities and proximity.
      </p>
      {isLoading ? (
        <div className="suggestions-popup-state">
          <div className="spinner" />
          <span>Finding suggestions...</span>
        </div>
      ) : error ? (
        <div className="suggestions-popup-state error">
          <p>Error: {error.message || 'Could not fetch suggestions.'}</p>
          <button type="button" className="retry-btn" onClick={handleFetchClick}>Retry</button>
        </div>
      ) : (
        <>
          <div className="suggestion-types">
            <SuggestionTypeRow
              signal="coattendee"
              icon="🎫"
              label="Event Co-Attendees"
              enabled={signals.coattendee.enabled}
              onEnabledChange={val => handleSignalChange('coattendee', 'enabled', val)}
            >
              <select
                value={signals.coattendee.minEvents}
                onChange={e => handleSignalChange('coattendee', 'minEvents', parseInt(e.target.value, 10))}
                disabled={!signals.coattendee.enabled}
              >
                <option value={2}>≥2 events</option>
                <option value={3}>≥3 events</option>
                <option value={5}>≥5 events</option>
              </select>
            </SuggestionTypeRow>
            <SuggestionTypeRow
              signal="geographic"
              icon="📍"
              label="Geographic Neighbors"
              enabled={signals.geographic.enabled}
              onEnabledChange={val => handleSignalChange('geographic', 'enabled', val)}
            >
              <div className="slider-container">
                <input
                  type="range"
                  min="0.1"
                  max="5"
                  step="0.1"
                  value={signals.geographic.radius}
                  onChange={e => handleSignalChange('geographic', 'radius', parseFloat(e.target.value))}
                  disabled={!signals.geographic.enabled}
                />
                <span>{signals.geographic.radius.toFixed(1)} mi</span>
              </div>
            </SuggestionTypeRow>
            <SuggestionTypeRow
              signal="employer"
              icon="🏢"
              label="Same Employer"
              enabled={signals.employer.enabled}
              onEnabledChange={val => handleSignalChange('employer', 'enabled', val)}
              disabled={false}
            >
            </SuggestionTypeRow>
          </div>
          <button
            type="button"
            className="fetch-suggestions-btn"
            onClick={handleFetchClick}
            disabled={isLoading}
          >
            {isLoading ? 'Finding...' : 'Find Suggestions'}
          </button>
        </>
      )}
    </div>
  );
};

export default SuggestionsPopup;
